/* @flow */
/* eslint no-useless-escape: 0 */

import type { $Configuration } from '../types/Configuration';
import type { $Translation } from '../types/Translation';
import type { $MediaQuery } from '../types/WorkbenchBreakpoints';
import type { $Theme } from '../constants/Themes';
import type { $HereMapsTheme } from '../components/MapProviders/HereMaps/HereMapsThemes';
import Globals from '../constants/Globals';
import Themes from '../constants/Themes';
import HereMapsThemes from '../components/MapProviders/HereMaps/HereMapsThemes';
import { WorkbenchMediaQueries } from '../constants/WorkbenchBreakpoints';

export const HREF_TARGET_BLANK = '_blank';

export const HREF_TARGET_SELF = '_self';

export const HREF_MAILTO = 'mailto:';

export const HREF_TEL = 'tel:';

export function convertStringToBoolean(input: ?string): boolean {
  return input === 'true';
}

export function getConfigurationPropertyValue(config: $Configuration, key: string): string {
  let propertyValue;
  if (key && config && config.properties && Array.isArray(config.properties)) {
    propertyValue = config.properties.find(obj => obj.key === key);
  }
  return propertyValue && propertyValue.value ? propertyValue.value : '';
}

export function getConfigurationTranslations(config: $Configuration, lang: string): Array<$Translation> {
  let translations;
  if (lang && config && config.languages) {
    translations = config.languages.filter(obj => obj.language === lang);
  }
  return translations && translations[0] && translations[0].translations ? translations[0].translations : [];
}

export function getConfigurationTranslationValue(translations: Array<$Translation>, key: string): string {
  const translationValue = translations && translations.find(obj => obj.key === key);
  return (translationValue && translationValue.translation) || '';
}

export function getConfigurationMarketProperty(config: $Configuration, market: string, propertyKey: string): ?any {
  return config && config.marketProperties && config.marketProperties[market] &&
  config.marketProperties[market][propertyKey] ? config.marketProperties[market][propertyKey] : null;
}

export function getLinkClickEventHandler(event: Event, cb: ?Function) {
  if (cb && isFunction(cb)) {
    event.preventDefault();
    cb();
  }
}

export function getLinkHref(link: ?string, isTel?: boolean = false): ?string {
  if (link) {
    if (link.indexOf('@') > -1) return `${HREF_MAILTO}${link}`;
    else if (isTel) return `${HREF_TEL}${link}`;
    else return link;
  }
  return null;
}

export function getLinkTarget(newBrowsingContext?: boolean): string {
  return newBrowsingContext && newBrowsingContext === true ? HREF_TARGET_BLANK : HREF_TARGET_SELF;
}

export function isHiddenField(settings: Object, key: string): boolean {
  return settings && settings.hiddenFields && settings.hiddenFields.indexOf(key) > -1;
}

export function isFunction(func: mixed): boolean {
  return !!func && {}.toString.call(func) === '[object Function]';
}


export function isUserAgentAndroid(userAgent: string): boolean {
  return /android/i.test(userAgent);
}

export function isUserAgentIOS(userAgent: string): boolean {
  return /iPad|iPhone|iPod/.test(userAgent);
}

export function isUserAgentIE(userAgent: string): boolean {
  return /MSIE|Trident/i.test(userAgent);
}

export function isPlatformMac(platform: string): boolean {
  return platform.toLowerCase().indexOf('mac') >= 0;
}

export function getDeepPropertyValue(path: any, obj: ?Object): ?string {
  return obj ? path.reduce((xs, x) => (xs && xs[x]) ? xs[x] : null, obj) : null;
}

export function getURLParams(name: string, urlString?: string): ?string {
  const url = new URL(urlString || window.location.href);
  const searchParams = url.searchParams;
  return searchParams.get(name);
}

export function encodeRequestQueryData(data: Object, keychain?: string) {
  return Object.keys(data).map((objectKey: string) => {
    const key = keychain ? keychain.concat('.' + objectKey) : objectKey;
    const value = data[objectKey];

    if (!value) {
      return null;
    }

    return (typeof value === 'object')
      ? encodeRequestQueryData(value, key)
      : [key, value].map(encodeURIComponent).join("=");
  })
  .filter(uriComponent => uriComponent !== null)
  .join("&");
}

export function getMapKeyByValue(map: Map<*, *>, searchValue: string) {
  for (let [ key, value ] of map.entries()) {
    if (value === searchValue) return key;
  }
}

export function getPackageVersion(packageJSON: Object, dependencyName: string) {
  return packageJSON.dependencies &&
    packageJSON.dependencies.hasOwnProperty(dependencyName) &&
    packageJSON.dependencies[dependencyName] || Globals.DEFAULT_VERSION;
}

export function mapWidgetThemeToHereMapsTheme(widgetTheme: ?$Theme): $HereMapsTheme {
  switch (widgetTheme) {

    case Themes.THEME_LIGHT:
    case Themes.THEME_TRANSPARENT:
      return HereMapsThemes.THEME_NORMAL_DAY_GREY;

    case Themes.THEME_DARK:
    case Themes.THEME_AMG:
      return HereMapsThemes.THEME_NORMAL_DAY_GREY;

    default:
      return Globals.DEFAULT_HERE_MAPS_THEME;
  }
}

export function overwriteURL(url: string): void {
  window.history.pushState({}, '', url);
}

export function dispatchEvent(eventName: string) {
  let event;
  if (typeof(Event) === 'function') {
    event = new Event(eventName);
  }
  else {
    event = document.createEvent('Event');
    event.initEvent(eventName, true, true);
  }
  window.dispatchEvent(event);
}

export function isMobile(mq: $MediaQuery): boolean {
  const _mq = mq.toUpperCase();
  return _mq === WorkbenchMediaQueries.MQ1 || _mq === WorkbenchMediaQueries.MQ2 || _mq === WorkbenchMediaQueries.MQ3;
}

export function setWidgetDynamicHeight(height?: ?string): Object {
  return height
    ? { maxHeight: height, overflowY: 'auto' }
    : { minHeight: '100%' };
}

export function setUserAgent(userAgent: string): void {
  /* $FlowFixMe */
  Object.defineProperty(navigator, 'userAgent', {
    get: function () {
      return userAgent;
    },
    configurable: true
  });
}
